import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Hammer, Car, Cpu, Shirt, Home, Wrench, Plus, Star, Eye, Users } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  automobile:   { label: "Automobile Engineering", Icon: Car,    color: "#FF2D7A" },
  crafts:       { label: "Crafts & Artisan",       Icon: Hammer, color: "#FFB42D" },
  electronics:  { label: "Electronics & Robotics", Icon: Cpu,    color: "#00E0C7" },
  fashion:      { label: "Fashion Design",         Icon: Shirt,  color: "#A45DFF" },
  architecture: { label: "Architecture & Furniture", Icon: Home, color: "#FF6B00" },
  other:        { label: "Other Builds",           Icon: Wrench, color: "#737B7E" },
};

const Makers = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ sort: "featured", limit: "30" });
    if (activeCategory) q.set("category", activeCategory);
    api.get(`/makers/showcases?${q.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Could not load the showcase"))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const setCategory = (cat) => {
    if (!cat) {
      searchParams.delete("category");
    } else {
      searchParams.set("category", cat);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="makers-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div
          className="absolute inset-0 opacity-90 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, #FFB42D 0%, transparent 45%), radial-gradient(circle at 10% 90%, #FF2D7A 0%, transparent 40%), linear-gradient(135deg, #0A0A0A 0%, #1a1816 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 py-14 sm:py-20 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.3em] font-semibold text-[#FFB42D] mb-4">
            <Hammer className="w-3 h-3" /> African Makers Exhibition
          </div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight max-w-3xl">
            Built by{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              hand. Seen
            </span>{" "}
            by the world.
          </h1>
          <p className="text-base text-white/75 max-w-2xl mt-4 leading-relaxed">
            From custom cars in Lagos to solar-powered prototypes in Nairobi, leather-work in
            Accra to robotics in Cape Town — showcase what you build, and meet the sponsors
            and investors looking for the next big African maker.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {user ? (
              <Link
                to="/makers/new"
                data-testid="makers-create-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0A0A0A] text-sm"
                style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
              >
                <Plus className="w-4 h-4" /> Submit your work
              </Link>
            ) : (
              <Link
                to={`/login?next=${encodeURIComponent("/makers/new")}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0A0A0A] text-sm bg-white"
              >
                Sign in to showcase
              </Link>
            )}
            <a
              href="#showcase-grid"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/25 text-white text-sm hover:bg-white/10"
            >
              Browse makers
            </a>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <div className="max-w-6xl mx-auto px-5 mt-8" id="showcase-grid">
        <div className="flex gap-2 flex-wrap" data-testid="makers-category-filter">
          <button
            onClick={() => setCategory(null)}
            data-testid="makers-cat-all"
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              !activeCategory
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
            }`}
          >
            All categories
          </button>
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const { Icon, label, color } = meta;
            const active = activeCategory === key;
            return (
              <button
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`makers-cat-${key}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  active
                    ? "text-white border-transparent"
                    : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                }`}
                style={active ? { background: color } : {}}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-5 pb-24 mt-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-stone-300 rounded-3xl bg-white" data-testid="makers-empty">
            <Hammer className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <div className="font-semibold text-stone-800">No showcases here yet</div>
            <div className="text-sm text-stone-500 mt-1">
              Be the first to put your craft on the exhibition floor.
            </div>
            {user && (
              <Link
                to="/makers/new"
                className="mt-5 inline-block px-5 py-2 rounded-full bg-stone-900 text-white text-sm font-bold"
              >
                Submit your work
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((s) => {
              const meta = CATEGORY_META[s.category] || CATEGORY_META.other;
              const { Icon } = meta;
              const cover = s.image_urls?.[0];
              return (
                <Link
                  key={s.showcase_id}
                  to={`/makers/${s.showcase_id}`}
                  data-testid={`makers-card-${s.showcase_id}`}
                  className="group block rounded-2xl bg-white border border-stone-200 overflow-hidden transition-shadow hover:shadow-xl"
                >
                  <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <Icon className="w-12 h-12" />
                      </div>
                    )}
                    {s.is_featured && (
                      <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFB42D] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider">
                        <Star className="w-2.5 h-2.5" /> Featured
                      </div>
                    )}
                    <div
                      className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ background: meta.color }}
                    >
                      <Icon className="w-2.5 h-2.5" /> {meta.label}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-stone-900 line-clamp-2 leading-snug">
                      {s.title}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                      {s.creator_name} · {s.country}
                      {s.region ? ` · ${s.region}` : ""}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> {s.sponsor_count} interested
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> {s.view_count}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Makers;
